import { CommonModule } from '@angular/common'
import { Component, Input, OnDestroy } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { ChangeRequest } from '@app/core/model/change-request.model'
import { ApplicationSetting } from '@app/core/model/enums/application-setting.enum'
import { ChangeRequestActions } from '@app/features/scope-overview/change-request/store/actions/change-request.actions'
import { ChangeRequestSelectors } from '@app/features/scope-overview/change-request/store/selectors/change-request.selectors'

import { ScopeOverviewTab } from '@app/features/scope-overview/enum/scope-overview-tab.enum'
import { ScopeTeamService } from '@app/features/scope-overview/service/scope-team.service'
import { LoaderModalModule } from '@app/shared/components/loader-modal/loader-modal.module'
import { DetailsOverviewComponent } from '@app/shared/components/ui-components/details-overview/details-overview/details-overview.component'
import { SharedModule } from '@app/shared/shared.module'
import { ScopeVersion } from '@core/model/scope-version'
import { User } from '@core/model/user.model'
import { Store } from '@ngrx/store'
import { ExchangeRateService } from '@shared/services/exchange-rate.service'
import { SnackbarService } from '@shared/utils/snackbar.service'
import { plainToInstance } from 'class-transformer'
import { combineLatest, filter, map, Observable, take } from 'rxjs'
import { ExchangeRate } from '../../model/exchange-rate.model'
import { Money } from '../../model/money.model'
import { ScopeRetainedHoursStats } from '../../model/scope-retained-hours-stats.model'
import { SowRetainedHoursStats } from '../../model/sow-retained-hours-stats.model'
import { ApprovalFlowService } from '../../service/approval-flow.service'
import { FindReplaceService } from '../../service/find-replace.service'
import { UserSettingsService } from '../../service/user-settings.service'
import { ScopeOverviewActions } from '../../store/actions/scope-overview.action'
import { ScopeOverviewSelectors } from '../../store/selectors/scope-overview.selector'

import { ScopeDetailsModule } from '../scope-details/scope-details.module'
import { ActivatedRoute } from '@angular/router'
import { ScopeOverviewContentComponent } from './scope-overview-content/scope-overview-content.component'
import { ScopeOverviewHeaderComponent } from './scope-overview-header/scope-overview-header.component'

@Component({
  selector: 'scope-overview',
  imports: [
    CommonModule,
    SharedModule,
    LoaderModalModule,
    DetailsOverviewComponent,
    ScopeOverviewContentComponent,
    ScopeOverviewHeaderComponent,
    ScopeDetailsModule,
  ],
  templateUrl: './scope-overview.component.html',
  styleUrls: ['./scope-overview.component.scss'],
  standalone: true,
})
export class ScopeOverviewComponent implements OnDestroy {
  @Input() id!: number

  currentScope$: Observable<ScopeVersion> = this.store$.select(ScopeOverviewSelectors.selectCurrentScope)
  currentChangeRequest$: Observable<ChangeRequest> = this.store$.select(
    ChangeRequestSelectors.selectCurrentChangeRequest
  )
  changeRequests$: Observable<ChangeRequest[]> = this.store$.select(ChangeRequestSelectors.selectChangeRequests)
  scopeRetainedHoursStats$: Observable<ScopeRetainedHoursStats> = this.store$.select(
    ScopeOverviewSelectors.selectScopeRetainedHourStats
  )
  sowRetainedHoursStats$: Observable<SowRetainedHoursStats> = this.store$.select(
    ScopeOverviewSelectors.selectSowRetainedHourStats
  )
  thirdPartyCostBalance$: Observable<Money> = this.store$.select(ScopeOverviewSelectors.selectThirdPartyCostBalance)

  isLoading$: Observable<boolean> = combineLatest([
    this.store$.select(ScopeOverviewSelectors.selectAllTasksLoading),
    this.store$.select(ChangeRequestSelectors.selectChangeRequestLoading),
    this.scopeApprovalFlowService.isApprovalLoadingSource$,
  ]).pipe(
    map(
      ([allTasksLoading, changeRequestLoading, approvalLoading]) =>
        allTasksLoading || changeRequestLoading || approvalLoading
    )
  )

  loggedInUser: User
  editScopeDescription = false
  exchangeRates: ExchangeRate[] = []

  constructor(
    private store$: Store,
    protected userSettingsService: UserSettingsService,
    private titleService: Title,
    protected scopeApprovalFlowService: ApprovalFlowService,
    protected findReplaceService: FindReplaceService,
    protected scopeTeamService: ScopeTeamService,
    protected route: ActivatedRoute,
    private snackbarService: SnackbarService,
    private exchangeRateService: ExchangeRateService
  ) {
    this.store$
      .select(ScopeOverviewSelectors.selectCurrentScope)
      .pipe(
        filter((currentScope) => !!currentScope),
        take(1)
      )
      .subscribe((currentScope) => {
        this.titleService.setTitle(currentScope.name + ' | Scope Overview | Scope')

        this.editScopeDescription =
          this.scopeApprovalFlowService.isStateInReview(currentScope.status) ||
          this.scopeApprovalFlowService.isStateReviewed(currentScope.status)

        this.setExchangeRates(currentScope)
      })
  }

  setExchangeRates(currentScope: ScopeVersion) {
    const CURRENCY_CONVERSION_KEY = 'scope_internal_currency_conversion'

    this.exchangeRateService.getExchangeRatesForSourceCurrency(currentScope.getRateCardCurrency()).subscribe({
      next: (availableExchangeRates) => {
        const savedCurrencyCode = currentScope.dynamicFields?.[CURRENCY_CONVERSION_KEY]

        if (
          savedCurrencyCode &&
          !availableExchangeRates.some((r: ExchangeRate) => r.toCurrencyCode === savedCurrencyCode)
        ) {
          availableExchangeRates = [...availableExchangeRates, new ExchangeRate(savedCurrencyCode)]
        }

        this.exchangeRates = availableExchangeRates

        const savedExchangeRate = this.exchangeRates.find(
          (rate: ExchangeRate) => rate.toCurrencyCode === savedCurrencyCode
        )
        this.exchangeRateService.exchangeRate =
          savedExchangeRate || new ExchangeRate(currentScope.getRateCardCurrency())
      },
      error: (error) => {
        this.snackbarService.showDefaultErrorSnackbar()
        return error
      },
    })
    this.exchangeRateService.exchangeRate = new ExchangeRate(currentScope.getRateCardCurrency())
  }

  setChangeRequest(changeRequest: ChangeRequest | null) {
    this.store$.dispatch(ChangeRequestActions.setCurrentChangeRequest({ changeRequest }))
  }

  setScopeVersion(scopeId: number, versionId: number) {
    this.store$.dispatch(ScopeOverviewActions.changeScopeVersion({ scopeId, versionId }))
  }

  saveScopeDescription(description: string, currentScope: ScopeVersion) {
    const updatedScope = plainToInstance(ScopeVersion, { ...currentScope, description })
    this.store$.dispatch(ScopeOverviewActions.updateScope({ scope: updatedScope }))
  }

  saveScopeNote(internalNote: string, currentScope: ScopeVersion) {
    const updatedScope = plainToInstance(ScopeVersion, { ...currentScope, internalNote })
    this.store$.dispatch(ScopeOverviewActions.updateScope({ scope: updatedScope }))
  }

  protected readonly ScopeOverviewTab = ScopeOverviewTab
  protected readonly ApplicationSetting = ApplicationSetting

  ngOnDestroy() {
    this.store$.dispatch(ChangeRequestActions.resetCurrentChangeRequest())
  }
}
